/**
 * SimpleSearchHandler - Gestionnaire simple pour le formulaire de recherche existant
 * Utilise directement le formulaire HTML existant et affiche les résultats
 * Inclut la détection de patterns de sécurité
 */

import { DemoDataService } from '../services/demo-data.js';
import { SearchCriteria } from '../../types/search-enhancement.js';

// Patterns de détection de sécurité
const SECURITY_PATTERNS = [
  { pattern: /admin/i, name: 'Admin Pattern', type: 'test' },
  { pattern: /\/etc\/[a-zA-Z0-9\/\-_.]+/i, name: 'Path Traversal', type: 'path_traversal' },
  { pattern: /OR\s+1\s*=\s*1/i, name: 'SQL Injection', type: 'sql_injection' },
  { pattern: /<script[^>]*>/i, name: 'XSS Attack', type: 'xss' },
  { pattern: /javascript\s*:\s*alert/i, name: 'XSS Alert', type: 'xss' }
];

/**
 * Gestionnaire simple de recherche
 */
export class SimpleSearchHandler {
  private form: HTMLFormElement;
  private resultsContainer: HTMLElement;

  constructor() {
    // Trouver le formulaire existant
    this.form = document.querySelector('.search-form') as HTMLFormElement;
    if (!this.form) {
      throw new Error('Formulaire de recherche non trouvé');
    }

    // Trouver ou créer le conteneur de résultats
    this.resultsContainer = this.findOrCreateResultsContainer();
    
    this.init();
  }

  /**
   * Initialise le gestionnaire
   */
  private init(): void {
    console.log('🔍 Initializing SimpleSearchHandler...');
    
    // Empêcher la soumission par défaut et gérer la recherche
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleSearch();
    });

    // Gérer le bouton retour du navigateur
    window.addEventListener('popstate', () => {
      this.loadSearchFromUrl();
    });

    // Charger les paramètres de recherche depuis l'URL au démarrage
    this.loadSearchFromUrl();

    console.log('✅ SimpleSearchHandler initialized');
  }

  /**
   * Trouve ou crée le conteneur de résultats
   */
  private findOrCreateResultsContainer(): HTMLElement {
    let container = document.querySelector('#search-results-container') as HTMLElement;
    
    if (!container) {
      // Le conteneur existe déjà dans le HTML, mais vérifions
      container = document.querySelector('.search-results-section') as HTMLElement;
    }

    if (!container) {
      throw new Error('Conteneur de résultats non trouvé');
    }

    return container;
  }

  /**
   * Vérifie si une valeur contient un pattern malveillant
   */
  private checkMaliciousPattern(value: string): { pattern: string; type: string } | null {
    if (!value || !value.trim()) return null;

    for (const p of SECURITY_PATTERNS) {
      if (p.pattern.test(value)) {
        return { pattern: p.name, type: p.type };
      }
    }
    return null;
  }

  /**
   * Gère la recherche
   */
  private async handleSearch(): Promise<void> {
    try {
      // Récupérer les données du formulaire
      const formData = new FormData(this.form);
      const destination = formData.get('destination') as string;
      const departureDate = formData.get('departureDate') as string;
      const returnDate = formData.get('returnDate') as string;
      const passengers = parseInt(formData.get('passengers') as string) || 2;

      // Vérification de sécurité sur la destination
      if (destination && destination.trim()) {
        const malicious = this.checkMaliciousPattern(destination.trim());
        if (malicious) {
          console.log(`🚨 Malicious pattern detected: ${malicious.pattern}`);
          console.log(`🔄 Redirecting to fake-admin.html...`);
          
          // Rediriger vers la page de leak
          setTimeout(() => {
            window.location.href = `fake-admin.html?attack=${malicious.type}&pattern=${encodeURIComponent(malicious.pattern)}`;
          }, 100);
          
          return;
        }
      }

      // Validation basique
      if (!destination || !destination.trim()) {
        alert('Please enter a destination');
        return;
      }

      if (!departureDate) {
        alert('Please select a departure date');
        return;
      }

      // Déterminer le type de recherche basé sur l'onglet actif
      const activeTab = this.form.querySelector('.search-form__tab--active') as HTMLElement;
      const searchType = activeTab?.dataset.searchType as 'flights' | 'hotels' | 'trains' || 'flights';

      // Créer les critères de recherche
      const criteria: SearchCriteria = {
        destination: destination.trim(),
        departureDate: new Date(departureDate),
        passengers: passengers,
        type: searchType
      };

      if (returnDate) {
        criteria.returnDate = new Date(returnDate);
      }

      console.log('🔍 Search criteria:', criteria);

      // Mettre à jour l'URL avec les paramètres de recherche
      this.updateUrlWithSearchParams(criteria);

      // Afficher l'état de chargement
      this.showLoading();

      // Effectuer la recherche
      const results = await DemoDataService.performSearch(criteria);
      
      console.log('📊 Search results:', results);

      // Afficher les résultats
      this.displayResults(results, destination);

    } catch (error) {
      console.error('Erreur de recherche:', error);
      this.showError('Une erreur est survenue lors de la recherche. Veuillez réessayer.');
    }
  }

  /**
   * Affiche l'état de chargement
   */
  private showLoading(): void {
    this.resultsContainer.style.display = 'block';
    this.resultsContainer.setAttribute('aria-hidden', 'false');
    
    this.resultsContainer.innerHTML = `
      <div class="search-results-container">
        <div class="search-loading">
          <div class="loading-spinner"></div>
          <h2>Searching...</h2>
          <p>We're finding the best deals for you</p>
        </div>
      </div>
    `;

    // Faire défiler vers les résultats
    this.resultsContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Affiche les résultats
   */
  private displayResults(results: any[], destination: string): void {
    if (results.length === 0) {
      this.showNoResults(destination);
      return;
    }

    const resultsHtml = `
      <div class="search-results-container">
        <div class="search-results-header">
          <h2>Search Results</h2>
          <p>${results.length} result${results.length > 1 ? 's' : ''} found for "${destination}"</p>
        </div>
        
        <div class="search-results-grid">
          ${results.map(result => this.createResultCard(result)).join('')}
        </div>
      </div>
    `;

    this.resultsContainer.innerHTML = resultsHtml;
  }

  /**
   * Crée une carte de résultat
   */
  private createResultCard(result: any): string {
    const availabilityClass = `availability-${result.availability}`;
    const availabilityText = this.getAvailabilityText(result.availability);
    
    return `
      <div class="result-card ${availabilityClass}">
        <div class="result-card-header">
          <h3 class="result-title">${result.title}</h3>
          <div class="result-price">
            <span class="price-amount">${result.price.amount}€</span>
          </div>
        </div>
        
        <div class="result-content">
          <p class="result-description">${result.description}</p>
          
          <div class="result-details">
            <div class="result-detail">
              <span class="detail-label">Provider:</span>
              <span class="detail-value">${result.provider}</span>
            </div>
            
            ${result.duration ? `
              <div class="result-detail">
                <span class="detail-label">Duration:</span>
                <span class="detail-value">${result.duration}</span>
              </div>
            ` : ''}
            
            ${result.rating ? `
              <div class="result-detail">
                <span class="detail-label">Rating:</span>
                <span class="detail-value">${result.rating}/5 ⭐</span>
              </div>
            ` : ''}
          </div>
          
          <div class="result-features">
            ${result.features.map((feature: string) => `
              <span class="feature-tag">${feature}</span>
            `).join('')}
          </div>
        </div>
        
        <div class="result-footer">
          <div class="availability-status ${availabilityClass}">
            ${availabilityText}
          </div>
          
          ${result.availability !== 'unavailable' ? `
            <button class="book-button" onclick="window.open('${result.bookingUrl}', '_blank')">
              Book Now
            </button>
          ` : `
            <button class="book-button" disabled>
              Unavailable
            </button>
          `}
        </div>
      </div>
    `;
  }

  /**
   * Retourne le texte de disponibilité
   */
  private getAvailabilityText(availability: string): string {
    switch (availability) {
      case 'available':
        return '✅ Available';
      case 'limited':
        return '⚠️ Limited seats';
      case 'unavailable':
        return '❌ Unavailable';
      default:
        return 'Unknown status';
    }
  }

  /**
   * Affiche l'état "aucun résultat"
   */
  private showNoResults(destination: string): void {
    this.resultsContainer.innerHTML = `
      <div class="search-results-container">
        <div class="no-results">
          <div class="no-results-icon">🔍</div>
          <h2>No results found</h2>
          <p>We couldn't find any results for "${destination}"</p>
          <p>Try a different destination or modify your search criteria.</p>
          <button class="retry-button" onclick="window.location.reload()">
            New search
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Affiche une erreur
   */
  private showError(message: string): void {
    this.resultsContainer.innerHTML = `
      <div class="search-results-container">
        <div class="search-error">
          <div class="error-icon">⚠️</div>
          <h2>Search Error</h2>
          <p>${message}</p>
          <button class="retry-button" onclick="window.location.reload()">
            Retry
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Met à jour l'URL avec les paramètres de recherche
   */
  private updateUrlWithSearchParams(criteria: SearchCriteria): void {
    const params = new URLSearchParams();
    
    params.set('destination', criteria.destination);
    params.set('departureDate', criteria.departureDate.toISOString().split('T')[0]!);
    params.set('passengers', criteria.passengers.toString());
    params.set('type', criteria.type);
    
    if (criteria.returnDate) {
      params.set('returnDate', criteria.returnDate.toISOString().split('T')[0]!);
    }

    // Mettre à jour l'URL sans recharger la page
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({ search: true }, '', newUrl);
  }

  /**
   * Charge les paramètres de recherche depuis l'URL
   */
  private loadSearchFromUrl(): void {
    const params = new URLSearchParams(window.location.search);
    
    const destination = params.get('destination');
    const departureDate = params.get('departureDate');
    const returnDate = params.get('returnDate');
    const passengers = params.get('passengers');
    const type = params.get('type');

    // Vérification de sécurité sur tous les paramètres URL
    const urlParams = [destination, departureDate, returnDate, passengers, type];
    for (const param of urlParams) {
      if (param) {
        const malicious = this.checkMaliciousPattern(param);
        if (malicious) {
          console.log(`🚨 Malicious pattern detected in URL parameter: ${malicious.pattern}`);
          console.log(`🔄 Redirecting to fake-admin.html...`);
          
          // Rediriger vers la page de leak
          setTimeout(() => {
            window.location.href = `fake-admin.html?attack=${malicious.type}&pattern=${encodeURIComponent(malicious.pattern)}&source=url_param`;
          }, 100);
          
          return;
        }
      }
    }

    // Si on a des paramètres de recherche, remplir le formulaire et effectuer la recherche
    if (destination && departureDate) {
      console.log('🔄 Loading search from URL parameters...');
      
      // Remplir le formulaire
      const destinationInput = this.form.querySelector('[name="destination"]') as HTMLInputElement;
      const departureDateInput = this.form.querySelector('[name="departureDate"]') as HTMLInputElement;
      const returnDateInput = this.form.querySelector('[name="returnDate"]') as HTMLInputElement;
      const passengersSelect = this.form.querySelector('[name="passengers"]') as HTMLSelectElement;

      if (destinationInput) destinationInput.value = destination;
      if (departureDateInput) departureDateInput.value = departureDate;
      if (returnDateInput && returnDate) returnDateInput.value = returnDate;
      if (passengersSelect && passengers) passengersSelect.value = passengers;

      // Mettre à jour l'onglet actif si spécifié
      if (type) {
        const tabs = this.form.querySelectorAll('.search-form__tab');
        tabs.forEach(tab => {
          const tabElement = tab as HTMLElement;
          const isActive = tabElement.dataset.searchType === type;
          tabElement.classList.toggle('search-form__tab--active', isActive);
          tabElement.setAttribute('aria-selected', isActive.toString());
        });
      }

      // Effectuer la recherche automatiquement
      this.performSearchFromUrl(destination, departureDate, returnDate, passengers, type);
    }
  }

  /**
   * Effectue une recherche basée sur les paramètres URL
   */
  private async performSearchFromUrl(
    destination: string, 
    departureDate: string, 
    returnDate: string | null, 
    passengers: string | null, 
    type: string | null
  ): Promise<void> {
    try {
      // Vérification de sécurité sur les paramètres URL
      const malicious = this.checkMaliciousPattern(destination.trim());
      if (malicious) {
        console.log(`🚨 Malicious pattern detected in URL: ${malicious.pattern}`);
        console.log(`🔄 Redirecting to fake-admin.html...`);
        
        // Rediriger vers la page de leak
        setTimeout(() => {
          window.location.href = `fake-admin.html?attack=${malicious.type}&pattern=${encodeURIComponent(malicious.pattern)}&source=url`;
        }, 100);
        
        return;
      }

      // Créer les critères de recherche
      const criteria: SearchCriteria = {
        destination: destination.trim(),
        departureDate: new Date(departureDate),
        passengers: parseInt(passengers || '2'),
        type: (type as 'flights' | 'hotels' | 'trains') || 'flights'
      };

      if (returnDate) {
        criteria.returnDate = new Date(returnDate);
      }

      console.log('🔍 Search from URL:', criteria);

      // Afficher l'état de chargement
      this.showLoading();

      // Effectuer la recherche
      const results = await DemoDataService.performSearch(criteria);
      
      console.log('📊 Search results from URL:', results);

      // Afficher les résultats
      this.displayResults(results, destination);

    } catch (error) {
      console.error('Error performing search from URL:', error);
      this.showError('An error occurred while loading the search. Please try again.');
    }
  }
}

/**
 * Initialise le gestionnaire de recherche simple
 */
export function initSimpleSearch(): void {
  // Attendre que le DOM soit chargé
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new SimpleSearchHandler();
    });
  } else {
    new SimpleSearchHandler();
  }
}